import { CheckCircleOutlined, Description, RadioButtonUnchecked } from '@mui/icons-material';
import { Box, Typography, Paper, CircularProgress, Chip, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import AgreementSection, { type AgreementStatus } from '../components/agreementSection';
import AgreementViewerModal from '../components/agreementViewerModal';
import CaseRetainersSections from '../components/caseRetainersSections';
import MilestoneTable from '../components/milestoneTable';
import PageBackground from 'components/v2/PageBackground';

import api from 'api';
import { retainerTypes } from 'constant';
import { isTemplateRetainerFlow, isVisaRetainerPending } from 'helpers/applicationRetainers';
import { useApplicationRetainers } from 'hooks/useApplicationRetainers';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import { AppDispatch, RootState } from 'redux/store';
import type {
    AgreementDocumentInterface,
    AssessmentAgreementStatus,
    MilestoneInterface,
} from 'types';
import { isAgreementSigningAvailable, toClientSignatureHtml } from 'utils/agreement';
import { getErrorMessage } from 'utils/v2/errorHelper';

const formatDate = (value?: string | Date | null) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const SignRetainer = () => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const selectedApplicationId = Number(params.id);

    const { translate } = useTranslate();
    const { modalObject, onResetModalObj, setModalObject } =
        useModalObj<AgreementDocumentInterface>();

    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { applications } = useSelector((state: RootState) => state.applications);
    const applicationsUserId = selectedUserId || user?.id;

    const [milestones, setMilestones] = useState<MilestoneInterface[]>([]);
    const [isLoadingMilestones, setIsLoadingMilestones] = useState(false);
    const [assessmentStatusData, setAssessmentStatusData] =
        useState<AssessmentAgreementStatus | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: retainersList } = useApplicationRetainers(
        Number.isNaN(selectedApplicationId) ? null : selectedApplicationId
    );
    const usesTemplateRetainers = isTemplateRetainerFlow(retainersList?.flow);

    const refreshAssessmentStatus = useCallback(async () => {
        try {
            const res = await api.assessmentAgreement.getAssessmentAgreementStatusForUser();
            setAssessmentStatusData(res.data as unknown as AssessmentAgreementStatus);
        } catch {
            // non-blocking
        }
    }, []);

    useEffect(() => {
        if (!selectedApplicationId || Number.isNaN(selectedApplicationId)) return;
        setIsLoadingMilestones(true);
        api.application.milestones
            .get(String(selectedApplicationId))
            .then((res) => setMilestones(res.data as unknown as MilestoneInterface[]))
            .catch(() => setMilestones([]))
            .finally(() => setIsLoadingMilestones(false));
    }, [selectedApplicationId]);

    useEffect(() => {
        void refreshAssessmentStatus();
    }, [refreshAssessmentStatus]);

    const selectedApplication = applications.find((app) => app.id === selectedApplicationId);
    const isOfferSent = selectedApplication?.date_offer_sent != null;

    const isAssessmentSigned = !!assessmentStatusData?.candidate_signed_date;
    const isVisaSigned = !!selectedApplication?.date_agreement_signed;

    const assessmentSignedOn = formatDate(assessmentStatusData?.candidate_signed_date);
    const visaSignedOn = formatDate(selectedApplication?.date_agreement_signed);

    const handleFetchEligibilityAssessment = useCallback(
        async (action: string) => {
            try {
                if (!user?.id) throw new Error('User ID is required');
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'Eligibility Assessment Agreement',
                    loading: true,
                }));
                const data = await api.signRetainer.getAssessmentAgreementDocument(user.id);
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'Eligibility Assessment Agreement',
                    loading: false,
                }));
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, 'Failed to load assessment agreement'),
                        type: 'error',
                    })
                );
                setModalObject((prev) => ({ ...prev, loading: false }));
            }
        },
        [dispatch, setModalObject, user?.id]
    );

    const handleFetchVisaAgreement = useCallback(
        async (action: string) => {
            try {
                if (!selectedApplicationId) throw new Error('Application ID is required');
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'Visa Application Retainer Agreement',
                    loading: true,
                }));
                const data = await api.signRetainer.getAgreementDocument(selectedApplicationId);
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'Visa Application Retainer Agreement',
                    loading: false,
                }));
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, 'Failed to fetch visa agreement'),
                        type: 'error',
                    })
                );
                setModalObject((prev) => ({ ...prev, loading: false }));
            }
        },
        [dispatch, setModalObject, selectedApplicationId]
    );

    const handleAction = useCallback(
        async (action: string) => {
            if (action === retainerTypes['Eligibility Assessment']) {
                await handleFetchEligibilityAssessment(action);
            } else if (action === retainerTypes['Visa Application']) {
                await handleFetchVisaAgreement(action);
            }
        },
        [handleFetchEligibilityAssessment, handleFetchVisaAgreement]
    );

    const handleUploadVisaAgreement = async (signature: string, signedAgreement: Blob | null) => {
        if (!selectedApplicationId || !isAgreementSigningAvailable(modalObject.data?.summary)) {
            return;
        }
        try {
            setIsSubmitting(true);
            setModalObject((prev) => ({ ...prev, disabled: true }));
            const formData = new FormData();
            if (modalObject.data?.summary.signing?.method === 'server_embedded') {
                // Template retainer: the backend embeds the signature and renders the PDF
                formData.append('client_signature', toClientSignatureHtml(signature));
            } else {
                if (!signedAgreement) throw new Error('Signed agreement is required');
                formData.append('retainer_agreement', signedAgreement, 'signed_agreement.pdf');
                formData.append('client_signature', toClientSignatureHtml(signature));
            }
            await api.signRetainer.postSignVisaAgreement(selectedApplicationId, formData);
            if (applicationsUserId) {
                await dispatch(loadApplications(applicationsUserId.toString()));
            }
            dispatch(setAlert({ message: 'Visa retainer signed successfully!', type: 'success' }));
            onResetModalObj();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to sign agreement'),
                    type: 'error',
                })
            );
            setModalObject((prev) => ({ ...prev, disabled: false }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadEligibilityAgreement = async (signedAgreement: Blob) => {
        if (!isAgreementSigningAvailable(modalObject.data?.summary)) return;
        try {
            setIsSubmitting(true);
            setModalObject((prev) => ({ ...prev, disabled: true }));
            const formData = new FormData();
            formData.append('assessment_retainer', signedAgreement, 'signed_agreement.pdf');
            await api.signRetainer.postSignAssessmentAgreement(formData);
            await refreshAssessmentStatus();
            dispatch(
                setAlert({
                    message: 'Eligibility assessment agreement signed successfully!',
                    type: 'success',
                })
            );
            onResetModalObj();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to sign agreement'),
                    type: 'error',
                })
            );
            setModalObject((prev) => ({ ...prev, disabled: false }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSign = (signature: string, signedAgreement: Blob | null) => {
        if (modalObject.action === retainerTypes['Visa Application']) {
            void handleUploadVisaAgreement(signature, signedAgreement);
        }
        if (modalObject.action === retainerTypes['Eligibility Assessment'] && signedAgreement) {
            void handleUploadEligibilityAgreement(signedAgreement);
        }
    };

    const loadingEligibility =
        modalObject.loading && modalObject.action === retainerTypes['Eligibility Assessment'];
    const loadingVisa =
        modalObject.loading && modalObject.action === retainerTypes['Visa Application'];

    const eligibilityConfig = useMemo(() => {
        const status: AgreementStatus = isAssessmentSigned ? 'signed' : 'ready';
        return {
            status,
            alertMessage: isAssessmentSigned
                ? assessmentSignedOn
                    ? `Eligibility assessment agreement signed on ${assessmentSignedOn}.`
                    : 'Eligibility assessment agreement has been signed.'
                : 'Eligibility assessment agreement is ready for your signature. This is separate from the visa retainer.',
            actionLabel: isAssessmentSigned ? 'View signed copy' : 'Review & sign',
            actionDisabled: false,
            actionLoading: loadingEligibility || isSubmitting,
            onAction: () => void handleAction(retainerTypes['Eligibility Assessment']),
        };
    }, [assessmentSignedOn, handleAction, isAssessmentSigned, isSubmitting, loadingEligibility]);

    const visaConfig = useMemo(() => {
        if (!isOfferSent) {
            return {
                status: 'unavailable' as AgreementStatus,
                alertMessage:
                    'Visa application retainer is not ready yet. Please contact support if you believe this is an error. Payment milestones below apply only when the retainer is issued.',
                actionLabel: 'Not available yet',
                actionDisabled: true,
                actionLoading: false,
                disabledReason:
                    'Your consultant has not issued the visa retainer offer for this application yet.',
                onAction: () => undefined,
            };
        }
        if (isVisaSigned) {
            return {
                status: 'signed' as AgreementStatus,
                alertMessage: visaSignedOn
                    ? `Visa application retainer signed on ${visaSignedOn}. Fees below belong to this retainer only.`
                    : 'Visa application retainer has been signed. Fees below belong to this retainer only.',
                actionLabel: 'View signed copy',
                actionDisabled: false,
                actionLoading: loadingVisa || isSubmitting,
                disabledReason: undefined as string | undefined,
                onAction: () => void handleAction(retainerTypes['Visa Application']),
            };
        }
        return {
            status: 'ready' as AgreementStatus,
            alertMessage:
                'Visa application retainer is ready for your signature. Payment milestones below apply only to this retainer.',
            actionLabel: 'Review & sign',
            actionDisabled: false,
            actionLoading: loadingVisa || isSubmitting,
            disabledReason: undefined as string | undefined,
            onAction: () => void handleAction(retainerTypes['Visa Application']),
        };
    }, [handleAction, isOfferSent, isSubmitting, isVisaSigned, loadingVisa, visaSignedOn]);

    const hasValidAppId = !!selectedApplicationId && !Number.isNaN(selectedApplicationId);
    const hasSignedCaseRetainer = Boolean(retainersList?.retainers.some((row) => row.signed));
    // Spouses can browse the case but only the candidate signs (API returns 403 otherwise)
    const canSignCaseRetainers = selectedApplication
        ? selectedApplication.candidate_id === user?.id
        : true;
    const caseRetainerChipSigned = usesTemplateRetainers ? hasSignedCaseRetainer : isVisaSigned;
    const caseRetainerChipLabel = usesTemplateRetainers
        ? translate('retainers')
        : isVisaSigned
          ? 'Visa signed'
          : isOfferSent
            ? 'Visa open'
            : 'Visa not ready';

    return (
        <PageBackground rootSx={{ overflow: 'auto' }}>
            {/* Compact page title */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.35rem', sm: '1.5rem' },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.25,
                        mb: 0.5,
                    }}
                >
                    Sign retainer agreements
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Two independent documents. Review each carefully — signing one does not complete
                    the other.
                </Typography>
            </Box>

            {!hasValidAppId ? (
                <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <Description sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Select an application
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose a case from the sidebar to review eligibility and visa retainers.
                    </Typography>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    {/* Application summary */}
                    {selectedApplication && (
                        <Box
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1.5, sm: 1.75 },
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'rgba(248, 250, 252, 0.7)',
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 1.25, sm: 2 }}
                                sx={{
                                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    {/* Line 1: Program */}
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 0.5 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {translate('program')}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 650,
                                                // Cooler slate than pure black for readability on light bg
                                                color: '#334155',
                                                lineHeight: 1.3,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {selectedApplication.program_name || 'Visa application'}
                                        </Typography>
                                    </Stack>

                                    {/* Line 2: Country + Application ID */}
                                    <Stack
                                        direction="row"
                                        spacing={{ xs: 1.5, sm: 2.5 }}
                                        useFlexGap
                                        sx={{ flexWrap: 'wrap', alignItems: 'baseline' }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            sx={{ alignItems: 'baseline' }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 600 }}
                                            >
                                                {translate('country')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: '#475569' }}
                                            >
                                                {selectedApplication.country || '—'}
                                            </Typography>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            sx={{ alignItems: 'baseline' }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 600 }}
                                            >
                                                Application ID
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: '#475569' }}
                                            >
                                                #{selectedApplicationId}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>

                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    useFlexGap
                                    sx={{ flexWrap: 'wrap', flexShrink: 0 }}
                                >
                                    <Chip
                                        size="small"
                                        icon={
                                            isAssessmentSigned ? (
                                                <CheckCircleOutlined sx={{ fontSize: 16 }} />
                                            ) : (
                                                <RadioButtonUnchecked sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        label={
                                            isAssessmentSigned
                                                ? 'Eligibility signed'
                                                : 'Eligibility open'
                                        }
                                        color={isAssessmentSigned ? 'success' : 'default'}
                                        variant={isAssessmentSigned ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 600, height: 26 }}
                                    />
                                    <Chip
                                        size="small"
                                        icon={
                                            caseRetainerChipSigned ? (
                                                <CheckCircleOutlined sx={{ fontSize: 16 }} />
                                            ) : (
                                                <RadioButtonUnchecked sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        label={caseRetainerChipLabel}
                                        color={caseRetainerChipSigned ? 'success' : 'default'}
                                        variant={caseRetainerChipSigned ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 600, height: 26 }}
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                    )}

                    <AgreementSection
                        title="1. Eligibility assessment"
                        description="Covers your eligibility assessment only. Signing this does not sign the visa retainer."
                        status={eligibilityConfig.status}
                        alertMessage={eligibilityConfig.alertMessage}
                        actionLabel={eligibilityConfig.actionLabel}
                        actionDisabled={eligibilityConfig.actionDisabled}
                        actionLoading={eligibilityConfig.actionLoading}
                        onAction={eligibilityConfig.onAction}
                    />

                    {usesTemplateRetainers ? (
                        <CaseRetainersSections
                            retainers={retainersList?.retainers ?? []}
                            visaRetainerPending={isVisaRetainerPending(retainersList)}
                            milestones={milestones}
                            isLoadingMilestones={isLoadingMilestones}
                            canSign={canSignCaseRetainers}
                            paymentStatus={selectedApplication?.payment_status}
                            onOpen={(retainer) =>
                                navigate(
                                    `/visa-process/${selectedApplicationId}/agreement/${retainer.agreement_id}`
                                )
                            }
                        />
                    ) : (
                        <AgreementSection
                            title="2. Visa application retainer"
                            description={`Covers visa services${
                                selectedApplication?.program_name
                                    ? ` for ${selectedApplication.program_name}`
                                    : ''
                            }${
                                selectedApplication?.country
                                    ? ` (${selectedApplication.country})`
                                    : ''
                            }. Payment milestones for this retainer are shown below.`}
                            status={visaConfig.status}
                            alertMessage={visaConfig.alertMessage}
                            actionLabel={visaConfig.actionLabel}
                            actionDisabled={visaConfig.actionDisabled}
                            actionLoading={visaConfig.actionLoading}
                            disabledReason={visaConfig.disabledReason}
                            onAction={visaConfig.onAction}
                            showDivider={false}
                        >
                            <Box
                                sx={{
                                    pt: 2,
                                    borderTop: '1px dashed',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {translate('paymentMilestones')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {translate('paymentMilestonesDescription')}
                                </Typography>

                                {isLoadingMilestones ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            py: 3,
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : (
                                    <MilestoneTable
                                        milestones={milestones}
                                        applicationPaymentStatus={
                                            selectedApplication?.payment_status
                                        }
                                    />
                                )}
                            </Box>
                        </AgreementSection>
                    )}
                </Paper>
            )}

            {[retainerTypes['Eligibility Assessment'], retainerTypes['Visa Application']].includes(
                modalObject.action as retainerTypes
            ) && (
                <AgreementViewerModal
                    data={modalObject.data ?? undefined}
                    showSignButton={isAgreementSigningAvailable(modalObject.data?.summary)}
                    title={modalObject.title}
                    loading={modalObject.loading}
                    disabled={modalObject.disabled || isSubmitting}
                    onClose={onResetModalObj}
                    onSign={onSign}
                />
            )}
        </PageBackground>
    );
};

export default SignRetainer;
